import type { ShipTime } from '@felix/domain';
import { Button, SegmentedControl, TextInput, UnstyledButton } from '@mantine/core';
import { IconAlertOctagon, IconPlus, IconSearch, IconTool } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { errorCode } from '../personnel/source.js';
import { SortableTh, type SortState, TablePager } from '../purchasing/table.js';
import condition from './Condition.module.css';
import type { DefectList, DefectView } from './condition-types.js';
import { DefectBadge, DefectDrawer, DefectSummary, NewDefectDrawer } from './DefectDrawer.js';
import {
  DEFECT_FILTERS,
  type DefectFilter,
  type DefectSortKey,
  defectStateKey,
  matchesDefectFilter,
  searchDefects,
  sortDefects,
} from './defect-view.js';
import { decisionKind, OfficeDecisionDrawer } from './OfficeDecisionDrawer.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { LoadFailed, PmsBadge, RowsSkeleton } from './PmsWorkParts.js';
import { paginate } from './plan-view.js';
import type { PmsSource } from './source.js';
import type { EquipmentTree, PendingItem, WorkOrderView } from './types.js';

/**
 * Arıza ve breakdown tablosu (plan M7 Task 21b): numara, bileşen, açıklama, ciddiyet, türetilmiş
 * durum ve bildirim günü; aranır, sıralanır ve sayfalanır. `items === null`: okunuyor.
 */
export function DefectTable({
  kind,
  items,
  names,
  onOpen,
  pageSize = 12,
}: {
  kind: 'defect' | 'breakdown';
  items: readonly DefectView[] | null;
  names: ReadonlyMap<string, string>;
  onOpen: (defect: DefectView) => void;
  pageSize?: number;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.condition.defects.${key}`);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortState<DefectSortKey>>({ key: 'no', dir: 'desc' });
  const [page, setPage] = useState(1);
  const rows = useMemo(
    () => sortDefects(searchDefects(items ?? [], query, names), sort, names),
    [items, query, sort, names],
  );
  const view = paginate(rows, page, pageSize);
  const columns: [DefectSortKey, string][] = [
    ['no', k('columns.no')],
    ['component', k('columns.component')],
    ['description', k('columns.description')],
    ['severity', k('columns.severity')],
    ['status', k('columns.status')],
    ['reported', k('columns.reported')],
  ];
  const title = kind === 'breakdown' ? k('titleBreakdowns') : k('title');
  return (
    <>
      <TextInput
        className={classes.workSearch}
        size="xs"
        placeholder={k('search')}
        aria-label={k('searchLabel')}
        leftSection={<IconSearch size={13} aria-hidden />}
        value={query}
        disabled={!items || items.length === 0}
        onChange={(event) => {
          setQuery(event.currentTarget.value);
          setPage(1);
        }}
      />
      <section
        className={classes.tableBox}
        aria-label={t('pms.equipment.tableOf', { name: title })}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={classes.table} data-compact data-cards>
          <thead>
            <tr>
              {columns.map(([key, label]) => (
                <SortableTh
                  key={key}
                  column={key}
                  label={label}
                  sort={sort}
                  onSort={(next) => {
                    setSort(next);
                    setPage(1);
                  }}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr>
                <td colSpan={columns.length} className={classes.emptyCell}>
                  <RowsSkeleton rows={4} />
                </td>
              </tr>
            ) : view.rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={classes.emptyCell}>
                  <EmptyState
                    icon={
                      kind === 'breakdown' ? (
                        <IconAlertOctagon size={22} stroke={1.8} />
                      ) : (
                        <IconTool size={22} stroke={1.8} />
                      )
                    }
                    title={
                      items.length === 0
                        ? kind === 'breakdown'
                          ? k('emptyBreakdowns')
                          : k('empty')
                        : k('noMatch')
                    }
                  />
                </td>
              </tr>
            ) : (
              view.rows.map((d) => (
                <tr key={d.id}>
                  <td data-label={k('columns.no')}>
                    <UnstyledButton className={work.linkButton} onClick={() => onOpen(d)}>
                      {d.defectNo}
                    </UnstyledButton>
                  </td>
                  <td data-label={k('columns.component')}>{names.get(d.equipmentId) ?? '—'}</td>
                  <td data-label={k('columns.description')} title={d.description}>
                    <span className={condition.cellText}>{d.description}</span>
                  </td>
                  <td data-label={k('columns.severity')}>
                    <DefectBadge value={String(d.severity)} kind="severity" />
                  </td>
                  <td data-label={k('columns.status')}>
                    <DefectBadge value={defectStateKey(d)} kind="state" />
                  </td>
                  <td data-label={k('columns.reported')}>{formatDay(d.localDate)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
      {items && rows.length > pageSize ? (
        <TablePager
          page={view.page}
          pages={view.pages}
          summary={t('pms.work.showing', { from: view.from, to: view.to, total: view.total })}
          label={t('pms.work.pages', { name: title })}
          onPage={setPage}
        />
      ) : null}
    </>
  );
}

type Data =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: DefectList; workOrders: WorkOrderView[]; pending: PendingItem[] };

export interface DefectsTabProps {
  source: PmsSource;
  vesselId: string;
  kind: 'defect' | 'breakdown';
  tree: EquipmentTree | null;
  timeZone: ShipTime;
  now: () => Date;
  query: URLSearchParams;
  setQuery: (patch: Record<string, string | null>) => void;
  /** Yazmadan sonra iş emirleri ve zil tazelenir. */
  onChanged: () => void;
}

/**
 * Work Orders ▸ Defects | Breakdowns (sayfa içi sekme; ADR 0027 §12; plan M7 Task 21b): durum
 * süzgeci, tablo, "+ Report Defect / Breakdown" (gemide; K13), arıza çekmecesi (`?defect=`), yeni
 * arıza (`?newDefect=1`). Web ofiste breakdown kapanışının süperintendent adımı karar çekmecesiyle
 * (`?decide=`; K24).
 */
export function DefectsTab(props: DefectsTabProps) {
  const { source, vesselId, kind, query, setQuery } = props;
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.condition.defects.${key}`);
  const [data, setData] = useState<Data>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const filter = (DEFECT_FILTERS as readonly string[]).includes(query.get('dstatus') ?? '')
    ? (query.get('dstatus') as DefectFilter)
    : 'all';

  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!source.defects) return;
    let live = true;
    setData((current) => (current.kind === 'ready' ? current : { kind: 'loading' }));
    Promise.all([
      source.defects(vesselId, { kind }),
      source.workOrders ? source.workOrders(vesselId).then((l) => l.items) : Promise.resolve([]),
      source.pending ? source.pending().catch(() => []) : Promise.resolve([]),
    ])
      .then(([list, workOrders, pending]) => {
        if (live) setData({ kind: 'ready', list, workOrders, pending });
      })
      .catch((error: unknown) => {
        if (live) setData({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, kind, reloads]);

  const reload = useCallback(() => {
    setReloads((n) => n + 1);
    props.onChanged();
  }, [props.onChanged]);
  const ready = data.kind === 'ready' ? data : null;
  const names = useMemo(
    () => new Map((props.tree?.nodes ?? []).map((n) => [n.id, n.name])),
    [props.tree],
  );
  const items = useMemo(
    () => (ready ? ready.list.items.filter((d) => matchesDefectFilter(d, filter)) : null),
    [ready, filter],
  );
  const canCreate =
    source.scope === 'ship' && Boolean(source.defectEditor) && (ready?.list.can?.create ?? false);
  const openId = query.get('defect');
  const opened = openId ? (ready?.list.items.find((d) => d.id === openId) ?? null) : null;
  const decideId = query.get('decide');
  // Ofis adımı (K24): breakdown kapanışının süperintendent kararı
  const officeItems = (ready?.pending ?? []).filter(
    (p) =>
      source.scope === 'web' &&
      p.office &&
      p.vesselId === vesselId &&
      decisionKind(p) === (kind === 'breakdown' ? 'breakdown' : 'defect') &&
      Boolean(source.office?.decideDefect),
  );
  const officeItem = decideId ? (officeItems.find((p) => p.recordId === decideId) ?? null) : null;
  const officeDefect = officeItem
    ? (ready?.list.items.find((d) => d.id === officeItem.recordId) ?? null)
    : null;

  if (!source.defects) return null;
  if (data.kind === 'error') {
    return (
      <Panel>
        <LoadFailed code={data.code} onRetry={() => setReloads((n) => n + 1)} />
      </Panel>
    );
  }
  return (
    <div className={work.drawerStack}>
      {officeItems.length > 0 ? (
        <Panel title={k('decision.panel')}>
          <ul className={work.pendingList}>
            {officeItems.map((item) => (
              <li key={item.recordId}>
                <span className={work.pendingText}>
                  <PmsBadge status="pending">
                    {t(`pms.work.decision.kind.${decisionKind(item)}`)}
                  </PmsBadge>
                  <span>
                    {item.defectNo ?? '—'}
                    {item.equipmentName ? ` — ${item.equipmentName}` : ''}
                  </span>
                </span>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => setQuery({ decide: item.recordId })}
                >
                  {k('decision.review')}
                </Button>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
      <Panel
        title={kind === 'breakdown' ? k('titleBreakdowns') : k('title')}
        icon={
          kind === 'breakdown' ? (
            <IconAlertOctagon size={18} stroke={1.8} />
          ) : (
            <IconTool size={18} stroke={1.8} />
          )
        }
        extra={
          canCreate ? (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} aria-hidden />}
              onClick={() => setQuery({ newDefect: '1' })}
            >
              {kind === 'breakdown' ? k('addBreakdown') : k('add')}
            </Button>
          ) : undefined
        }
      >
        <div className={work.toolbar}>
          <SegmentedControl
            size="xs"
            aria-label={k('filterLabel')}
            value={filter}
            onChange={(value) => setQuery({ dstatus: value === 'all' ? null : value })}
            data={DEFECT_FILTERS.map((key) => ({ value: key, label: k(`filters.${key}`) }))}
            className={work.statusFilter}
          />
        </div>
        <DefectTable
          kind={kind}
          items={items}
          names={names}
          onOpen={(d) => setQuery({ defect: d.id })}
        />
      </Panel>

      <DefectDrawer
        opened={opened !== null}
        defect={opened}
        source={source}
        tree={props.tree}
        workOrders={ready?.workOrders ?? []}
        pending={ready?.pending ?? []}
        can={ready?.list.can}
        onClose={() => setQuery({ defect: null })}
        onChanged={reload}
        onViewWorkOrder={(id) => setQuery({ tab: null, defect: null, wo: id })}
      />
      {canCreate ? (
        <NewDefectDrawer
          opened={query.get('newDefect') === '1'}
          kind={kind}
          tree={props.tree}
          timeZone={props.timeZone}
          now={props.now}
          master={ready?.list.can?.master}
          onClose={() => setQuery({ newDefect: null })}
          onSubmit={async (input) => {
            const created = await source.defectEditor?.create(input);
            setQuery({ newDefect: null, defect: created?.defect.id ?? null });
            reload();
          }}
        />
      ) : null}
      {source.scope === 'web' ? (
        <OfficeDecisionDrawer
          opened={officeItem !== null}
          item={officeItem}
          onClose={() => setQuery({ decide: null })}
          details={
            officeDefect ? (
              <DefectSummary
                defect={officeDefect}
                equipmentName={names.get(officeDefect.equipmentId) ?? null}
              />
            ) : null
          }
          onDecide={async (item, input) => {
            await source.office?.decideDefect?.(item.recordId, input);
            setQuery({ decide: null });
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
